import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable, catchError, map, of, switchMap, tap } from 'rxjs';
import {
  askForChallengeSuccess,
  loadUserAuthenticated,
  loadUserAuthenticatedError,
  loadUserAuthenticatedSuccess,
  loadUserAuthenticatedUnenriched,
  postForgotPassword,
  postForgotPasswordError,
  postForgotPasswordSuccess,
  postLogin,
  postLoginAccountNotProvisioned,
  postLoginAccountStatusRefused,
  postLoginError,
  postLoginMfa,
  postLoginMfaError,
  postLoginMfaSuccess,
  postLoginSuccess,
  postLoginUnavailable,
  postTokens,
  postTokensError,
  postTokensSuccess,
  redeemSsoHandoff,
  redeemSsoHandoffError,
  redeemSsoHandoffSuccess,
  requestOtpChallenge,
  requestOtpChallengeError,
  requestOtpChallengeSuccess,
  verifyOtpChallenge,
  verifyOtpChallengeError,
  verifyOtpChallengeSuccess,
} from '../actions/authentication.action';
import { AuthenticationService } from 'brain-data/service/auth/authentication.service';
import { Router } from '@angular/router';
import { getMfaUrl, getPortalHomeUrl, getUnenrichedSessionUrl } from 'shared/services/utils/url/url.service';
import { loadFacilitiesAtStartup } from '../actions/facility.actions';
import { CreateTokenOutcome, TokenDTO } from '@brain-auth/models/auth-token-dto.model';

@Injectable()
export class AuthenticationEffects {
  postLoginEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(postLogin),
      switchMap(({ username, password }) =>
        this.authenticationService.login(username, password).pipe(
          switchMap(loginResponse =>
            this.authenticationService.saveStorageAndLoadUserProfile(loginResponse).pipe(
              map(() => postLoginSuccess()),
              tap(() => (document.location = getPortalHomeUrl())),
              catchError(error => of(postLoginError({ error }))),
            ),
          ),
          catchError(error => {
            const tokenDto = error?.error as TokenDTO | undefined;

            if (tokenDto?.outcome === CreateTokenOutcome.MFA_REQUIRED) {
              return of(askForChallengeSuccess({ tokenDto })).pipe(
                tap(() => void this.router.navigate([getMfaUrl()])),
                catchError(err => of(postLoginError({ error: err }))),
              );
            }

            return this.classifyLoginFailure(error);
          }),
        ),
      ),
    );
  });

  postLoginMfaEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(postLoginMfa),
      switchMap(({ tokenDto }) =>
        this.authenticationService.loginMfa(tokenDto).pipe(
          switchMap(loginResponse =>
            this.authenticationService.saveStorageAndLoadUserProfile(loginResponse).pipe(
              map(userAuthenticated => postLoginMfaSuccess({ userAuthenticated: (userAuthenticated as any).info })),
              map(() => loadFacilitiesAtStartup()),
              tap(() => (document.location = getPortalHomeUrl())),
            ),
          ),
          // The MFA completion route is the SECOND converged issuance site, so it reaches the same
          // two non-credential failures the password route does and must discriminate identically.
          // Its own banner ("Incorrect code. Please try again.") is just as wrong for a KMS outage
          // as the login card's is.
          catchError(error => this.classifyLoginFailure(error, postLoginMfaError)),
        ),
      ),
    );
  });

  loadUserAuthenticatedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(loadUserAuthenticated),
      map(() => {
        const userAuthenticated = this.authenticationService.userAuthenticated();
        if (userAuthenticated) {
          return loadUserAuthenticatedSuccess({ userAuthenticated });
        }
        // BRN-2208: "no user" was reported for two opposite situations -- nobody signed in, and
        // somebody signed in whose token was never enriched. Only the first is an absent session.
        if (this.authenticationService.sessionState() === 'unenriched') {
          return loadUserAuthenticatedUnenriched();
        }
        return loadUserAuthenticatedError({ error: new Error('no user') });
      }),
    );
  });

  postForgotPasswordEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(postForgotPassword),
      switchMap(({ email }) => {
        return this.authenticationService.askForPasswordChange(email).pipe(
          map(() => postForgotPasswordSuccess()),
          catchError(error => of(postForgotPasswordError({ error }))),
        );
      }),
    );
  });

  postTokensEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(postTokens),
      switchMap(({ payload }) =>
        this.authenticationService.postTokens(payload).pipe(
          map(response => postTokensSuccess({ response })),
          catchError(error => of(postTokensError({ error }))),
        ),
      ),
    );
  });

  requestOtpChallengeEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(requestOtpChallenge),
      switchMap(() =>
        this.authenticationService.requestOtpChallenge().pipe(
          map(() => requestOtpChallengeSuccess()),
          catchError(error => of(requestOtpChallengeError({ error }))),
        ),
      ),
    );
  });

  verifyOtpChallengeEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(verifyOtpChallenge),
      switchMap(({ email, code, facilityGroupId }) =>
        this.authenticationService.verifyOtpChallenge(email, code, facilityGroupId).pipe(
          map(() => verifyOtpChallengeSuccess()),
          catchError(error => of(verifyOtpChallengeError({ error }))),
        ),
      ),
    );
  });

  // BRN-2148: SSO callback landing — redeem the handoff code, then reuse the SAME
  // saveStorageAndLoadUserProfile()/full-page-navigation shape the password flow already uses,
  // so an SSO session is established through the identical code path (AC1 parity).
  redeemSsoHandoffEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(redeemSsoHandoff),
      switchMap(({ code }) =>
        this.authenticationService.redeemSsoHandoff(code).pipe(
          switchMap((tokenDto: TokenDTO) =>
            this.authenticationService.saveStorageAndLoadUserProfile(tokenDto).pipe(
              map(() => redeemSsoHandoffSuccess()),
              tap(() => (document.location = getPortalHomeUrl())),
              catchError(error => of(redeemSsoHandoffError({ error }))),
            ),
          ),
          catchError(error => of(redeemSsoHandoffError({ error }))),
        ),
      ),
    );
  });

  /**
   * BRN-2208 L5 finding F1 — the discrimination the login paths never had.
   *
   * Before this, ONE undiscriminated `catchError` turned every failure that was not `MFA_REQUIRED`
   * into `postLoginError`, and the reducer turned that into the single fixed banner "The user name
   * or password is incorrect". Three different situations were therefore reported as a wrong
   * password:
   *
   * 1. a genuine credential failure — correct, and still the default below;
   * 2. a post-verification refusal (`FAILURE_ACCOUNT_NOT_PROVISIONED`), where the password was
   *    RIGHT and the only remedy the card offers cannot fix anything, so the user loops;
   * 3. a 5xx or transport failure — including a Cloud KMS outage, which convergence puts on this
   *    exact critical path, so an incident would tell every affected user their password was wrong.
   *
   * Order matters: transport/platform is checked FIRST, because a 5xx body is not a `TokenDTO` and
   * must never be read as one. Fail-closed is untouched — every branch here still refuses; only the
   * diagnosis and the destination change.
   */
  private classifyLoginFailure(
    error: unknown,
    credentialFailure: (props: { error: Error }) => Action = postLoginError,
  ): Observable<Action> {
    if (isPlatformFailure(error)) {
      return of(postLoginUnavailable({ error }));
    }

    const outcome = (error as { error?: TokenDTO } | null)?.error?.outcome;

    if (outcome === CreateTokenOutcome.FAILURE_ACCOUNT_NOT_PROVISIONED) {
      return of(postLoginAccountNotProvisioned()).pipe(tap(() => void this.router.navigate([getUnenrichedSessionUrl()])));
    }

    // BRN-2208 L6 finding L4-R: an ARCHIVED or BLOCKED account. Deliberately does NOT navigate --
    // /session/unenriched is the enrichment story and would be a second false diagnosis. The user
    // stays on the card they submitted from and is told what is actually true about their account.
    if (outcome === CreateTokenOutcome.FAILURE_WRONG_USER_STATUS) {
      return of(postLoginAccountStatusRefused());
    }

    return of(credentialFailure({ error: error as Error }));
  }

  constructor(
    private router: Router,
    private actions$: Actions,
    private authenticationService: AuthenticationService,
  ) {}
}

/**
 * A failure the user's credentials had nothing to do with. `status === 0` is Angular's shape for a
 * request that never got an HTTP answer at all (offline, DNS, TLS, CORS preflight); `>= 500` is the
 * server failing to answer one. Deliberately NOT keyed on any message text or error body — a 5xx
 * body is whatever the proxy or container happened to emit, and matching on its prose is how this
 * kind of check silently stops working.
 */
function isPlatformFailure(error: unknown): boolean {
  if (!(error instanceof HttpErrorResponse)) {
    return false;
  }

  return error.status === 0 || error.status >= 500;
}
